import React from "react";
import {
    Box,
    useMediaQuery,
    useTheme,
    alpha, Container,
} from "@mui/material";

// Images
import origins from "../../assets/essence/origins.png";
import family from "../../assets/essence/family.png";
import mexicanRoots from "../../assets/essence/mexicanRoots.png";
import conservas from "../../assets/essence/conservas.png";
import expansion from "../../assets/essence/expansion.png";
import today from "../../assets/essence/today.png";
import whatWeMake1 from "../../assets/essence/whatWeMake1.jpg";
import whatWeMake2 from "../../assets/essence/whatWeMake2.jpg";
import whyWeExist from "../../assets/essence/whyWeExist.jpg";
import whoWeServe1 from "../../assets/essence/whoWeServe1.jpg";
import whoWeServe2 from "../../assets/essence/whoWeServe2.jpg";

import ImageDataCarousel from "./Carousels/ImageDataCarousel";
import TwoByTwoImageDataCarousel from "./Carousels/TwoByTwoImageDataCarousel";
import OverlappedImagePair from "../layout/Images/OverlappedImagePair";

const storyItems = [
    {
        buttonTitle: "1963",
        title: "ORIGINS",
        subtitle: "Where San Miguel Began",
        description:
            "In 1963, Don Antonio bought a ranch with a simple belief: good food starts with the land. " +
            "He planted peach trees and began farming with care and passion. San Miguel started not as a brand, " +
            "but as a family farm, exporting peaches and apples with pride. The principle was clear: leave the land " +
            "better than you found it.",
        imageUrl: origins,
    },
    {
        buttonTitle: "1966",
        title: "FAMILY",
        subtitle: "Work, Family, and Purpose",
        description:
            "In 1966, Patricio Sr. asked Don Antonio for a job at the ranch so he could marry. On September 24, 1966, " +
            "he married and committed his life not only to his family but to the work of San Miguel. He worked there every " +
            "day until his passing in 2020. San Miguel grew with dedication and responsibility, shaped by the belief that " +
            "work is a way to honor la familia.",
        imageUrl: family,
    },
    {
        buttonTitle: "1969",
        title: "MEXICAN ROOTS",
        subtitle: "The Moment Mexico Took Root",
        description: "In 1969, San Miguel began harvesting jalapeño peppers, marking the shift from fruit farming to sharing authentic Mexican ingredients with the world. These jalapeños became more than a crop; they connected us to culture, flavor, and identity, bringing true Mexican ingredients to tables far beyond the ranch.",
        imageUrl: mexicanRoots,
    },
    {
        buttonTitle: "1971",
        title: "CONSERVAS",
        subtitle: "Preserving What Matters",
        description: "In 1971, Conservas San Miguel was founded. The business evolved from farming into food production with a clear mission: preserve Mexican ingredients for life. From artichokes and tomatoes to jalapeños and huitlacoche, San Miguel focused on flavor, quality, and tradition. The goal was never just to scale, it was about consistency and respect for ingredients.",
        imageUrl: conservas,
    },
    {
        buttonTitle: "1993",
        title: "EXPANSION",
        subtitle: "Growing With the Next Generation",
        description: "In 1993, Don Patricio’s sons; Patricio, Alejandro, and Gerardo joined the business, transforming San Miguel into an international operation. They expanded production and innovation while staying rooted in the same values that defined the ranch. From 1993 to 2024, San Miguel grew into a global food company.",
        imageUrl: expansion,
    },
    {
        buttonTitle: "TODAY",
        title: "TODAY",
        subtitle: "A Modern Vision, Same Roots",
        description: "In 2024, the great-grandsons of Don Antonio; Patricio, José Pablo, Rodrigo, and Julián, entered the business and launched San Miguel Family Foods in the United States. With deep respect for the past and a clear vision for the future, they are introducing a modern ready-to-eat products built on clean labels, authentic recipes, and convenience for today’s consumers.",
        imageUrl: today,
    }
];

const timelineItems = [
    {
        buttonTitle: "WHAT WE MAKE",
        title: "WHAT WE MAKE",
        subtitle: "Modern Mexican meals, made to fit real life.",
        description:
            "San Miguel USA creates ready-to-eat Mexican meals designed for today’s pace of life. We focus on clean-label recipes, authentic Mexican flavors, and formats that make real food easy to enjoy, anywhere, anytime.\n\n" +
            "Our products are built for convenience without compromise, shelf-stable, quick to prepare, and crafted to deliver flavor, nutrition, and simplicity in every bite.\n",
        renderImage: () => <WhatWeMakeOverlappedImage />,
    },
    {
        buttonTitle: "WHY WE EXIST",
        title: "WHY WE EXIST",
        subtitle: "Because real food should not be a tradeoff.",
        description:
            "People are busier than ever, but that should not mean giving up on real food. Modern convenience foods have lost their roots, relying on shortcuts that sacrifice authenticity, nutrition, and trust.\n\n" +
            "We exist to bring Mexican food back to what it was always meant to be, honest ingredients, real recipes, and nourishment you can feel good about, made convenient for modern life.\n",
        imageUrl: whyWeExist,
    },
    {
        buttonTitle: "WHO WE SERVE",
        title: "WHO WE SERVE",
        subtitle: "Our community is our family.",
        description:
            "People are busier than ever, but that should not mean giving up on real food. Modern convenience foods have lost their roots, relying on shortcuts that sacrifice authenticity, nutrition, and trust.\n\n" +
            "We exist to bring Mexican food back to what it was always meant to be, honest ingredients, real recipes, and nourishment you can feel good about, made convenient for modern life.\n",
        renderImage: () => <WhoWeServeOverlappedImage />,
    }
];

function WhatWeMakeOverlappedImage() {
    const theme = useTheme();

    return (
        <Box sx={{ py: 1, width: "100%", display: "flex", justifyContent: "center" }}>
            <Box sx={{ width: "100%", maxWidth: {xs: 300, sm: 400, md: 500, lg: 500, xl: 450} }}>
                <OverlappedImagePair
                    primarySrc={whatWeMake1}
                    secondarySrc={whatWeMake2}
                    border={`1px solid ${theme.palette.primary.main}`}
                    borderRadius={5}
                    height={{xs: 200, sm: 250, md: 350, lg: 360, xl: 320}}
                    secondaryTransform="rotate(3deg)"
                    secondaryAnchor={{ top: "0%", right: "2%" }}
                />
            </Box>
        </Box>
    );
}

function WhoWeServeOverlappedImage() {
    const theme = useTheme();

    return (
        <Box sx={{ py: 1, width: "100%", display: "flex", justifyContent: "center" }}>
            <Box sx={{ width: "100%", maxWidth: {xs: 280, sm: 350, md: 500, lg: 480, xl: 450} }}>
                <OverlappedImagePair
                    primarySrc={whoWeServe1}
                    secondarySrc={whoWeServe2}
                    border={`1px solid ${theme.palette.primary.main}`}
                    borderRadius={5}
                    height={{xs: 190, sm: 240, md: 350, lg: 330, xl: 300}}
                    secondaryTransform="rotate(-3deg)"
                    secondaryAnchor={{ top: "8%", right: "-2%" }}
                />
            </Box>
        </Box>
    );
}

function VerticalTiledDivider({ image, width = 26, flip = true }) {
    return (
        <Box
            sx={{
                flex: "0 0 auto",
                width,
                alignSelf: "stretch", // ✅ stretches to content height in grid
                backgroundImage: image ? `url(${image})` : "none",
                backgroundColor: image ? "transparent" : alpha("#000", 0.08),
                backgroundRepeat: "repeat-y",
                backgroundPosition: "bottom center",
                backgroundSize: "100% auto",
                transform: flip ? "rotate(180deg)" : "none",
                transformOrigin: "center",
            }}
        />
    );
}

function HorizontalTiledDivider({ image, height = 14 }) {
    return (
        <Box
            sx={{
                width: "100%",
                height,
                backgroundImage: image ? `url(${image})` : "none",
                backgroundColor: image ? "transparent" : alpha("#000", 0.08),
                backgroundRepeatX: "repeat",
                backgroundRepeatY: "no-repeat",
                backgroundPosition: "left center",
                backgroundSize: "auto 100%",
            }}
        />
    );
}

export default function HistoryTimeLine({
                                            dividerImageVertical,
                                            dividerImageHorizontal,
                                        }) {
    const theme = useTheme();

    // ✅ Desktop only on lg/xl
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
    const isMobileLayout = !isDesktop; // xs/sm/md

    React.useEffect(() => {
        const srcs = [
            ...storyItems.map(i => i.imageUrl).filter(Boolean),
            ...timelineItems.map(i => i.imageUrl).filter(Boolean),
        ];

        srcs.forEach((src) => {
            const img = new Image();
            img.src = src;
        });
    }, []);

    const desktopDividerWidth = 45;
    const mobileDividerThickness = 35;

    // -------------------------
    // MOBILE LAYOUT (xs/sm/md)
    // -------------------------
    if (isMobileLayout) {
        return (
            <Box sx={{ width: "100%" }}>
                <Container maxWidth="lg" sx={{paddingBottom: 1}}>
                    <TwoByTwoImageDataCarousel
                        items={storyItems}
                        height={{ xs: 600, sm: 550, md: 520 }}
                        initialIndex={0}
                        orientation="auto"
                    />
                </Container>

                <HorizontalTiledDivider image={dividerImageHorizontal} height={mobileDividerThickness}/>

                <Container maxWidth="lg" sx={{paddingY: 1}}>
                    <ImageDataCarousel
                        items={timelineItems}
                        initialIndex={0}
                        orientation="auto" // md↓ horizontal, lg↑ vertical (default behavior)
                        height={{ xs: 420, sm: 480, md: 580 }}
                    />
                </Container>
            </Box>
        );
    }

    // -------------------------
    // DESKTOP LAYOUT (lg/xl)
    // -------------------------
    return (
        <Box sx={{ width: "100%" }}>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: `1fr ${desktopDividerWidth}px 1fr`,
                    alignItems: "stretch",
                    gap: 0,
                }}
            >
                {/* LEFT */}
                <Container maxWidth="lg">
                    <TwoByTwoImageDataCarousel
                        items={storyItems}
                        height={{ lg: 620, xl: 580 }}
                        initialIndex={0}
                        orientation="auto"
                    />
                </Container>

                {/* CENTER DIVIDER */}
                <VerticalTiledDivider image={dividerImageVertical} width={desktopDividerWidth} flip />

                {/* RIGHT */}
                <Container maxWidth="lg">
                    <ImageDataCarousel
                        items={timelineItems}
                        initialIndex={0}
                        orientation="auto" // md↓ horizontal, lg↑ vertical (default behavior)
                        height={{ lg: 620, xl: 580 }}
                    />
                </Container>
            </Box>
        </Box>
    );
}
